import React, { useState, useEffect } from 'react';
import { projectFirestore } from '../firebase/config';
import { getThumbUrl, handleImageError } from '../utils/imageHelper';
import useSections from '../hooks/useSections';
import useImageSource from '../hooks/useImageSource';

const ImageManager = () => {
    const { sections: allSections } = useSections();
    const { config: sourceConfig, getDisplayUrl, getFallbackUrls } = useImageSource();
    const COLLECTIONS = allSections.filter(s => s.enabled !== false).map(s => ({ value: s.key, label: s.label }));
    const [collection, setCollection] = useState('images');
    const [docs, setDocs] = useState([]);
    const [deleting, setDeleting] = useState({});
    const [previewImg, setPreviewImg] = useState(null);
    const [reordering, setReordering] = useState(false);

    useEffect(() => {
        // نجلب كل المستندات بدون orderBy حتى لا يتم استبعاد المستندات التي ليس لها حقل order
        const unsub = projectFirestore.collection(collection)
            .onSnapshot((snap) => {
                let documents = [];
                snap.forEach(doc => {
                    documents.push({ ...doc.data(), id: doc.id });
                });
                // ترتيب محلي: حسب order (تنازلي) ثم createdAt (تنازلي)
                documents.sort((a, b) => {
                    const aOrder = a.order ?? 0;
                    const bOrder = b.order ?? 0;
                    if (aOrder !== bOrder) return bOrder - aOrder;
                    const aTime = a.createdAt?.seconds ?? 0;
                    const bTime = b.createdAt?.seconds ?? 0;
                    return bTime - aTime;
                });
                setDocs(documents);
            });
        return () => unsub();
    }, [collection]);

    // تحديد مصدر الصورة وعرض بادج مناسب
    const getSourceBadge = (doc) => {
        const sources = doc.sources || {};
        const keys = Object.keys(sources);
        if (keys.length > 1) {
            return { text: '☁ ' + keys.length + ' مصادر', bg: '#198754', title: keys.map(k => {
                const ca = (sourceConfig.cloudinaryAccounts || []).find(a => a.id === k);
                const fa = (sourceConfig.firebaseAccounts || []).find(a => a.id === k);
                return ca ? '☁ ' + ca.label : fa ? '🔥 ' + fa.label : k;
            }).join('\n') };
        }
        const url = doc.url || '';
        if (url.includes('res.cloudinary.com')) {
            const accLabel = keys.length === 1
                ? ((sourceConfig.cloudinaryAccounts || []).find(a => a.id === keys[0]) || {}).label || 'Cloudinary'
                : 'Cloudinary';
            return { text: '☁ ' + accLabel, bg: '#0d6efd', title: accLabel };
        }
        if (url.includes('firebasestorage.googleapis.com')) {
            const accLabel = keys.length === 1
                ? ((sourceConfig.firebaseAccounts || []).find(a => a.id === keys[0]) || {}).label || 'Firebase'
                : 'Firebase';
            return { text: '🔥 ' + accLabel, bg: '#fd7e14', title: accLabel };
        }
        if (keys.length === 1) {
            const ca = (sourceConfig.cloudinaryAccounts || []).find(a => a.id === keys[0]);
            const fa = (sourceConfig.firebaseAccounts || []).find(a => a.id === keys[0]);
            if (ca) return { text: '☁ ' + ca.label, bg: '#0d6efd', title: ca.label };
            if (fa) return { text: '🔥 ' + fa.label, bg: '#fd7e14', title: fa.label };
        }
        return { text: '❓ غير محدد', bg: '#6c757d', title: 'مصدر غير معروف' };
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) return;
        setDeleting(prev => ({ ...prev, [docId]: true }));
        try {
            await projectFirestore.collection(collection).doc(docId).delete();
        } catch (err) {
            alert('حدث خطأ أثناء الحذف: ' + err.message);
        }
        setDeleting(prev => ({ ...prev, [docId]: false }));
    };

    const handleMoveUp = async (index) => {
        if (index === 0) return;
        const current = docs[index];
        const prev = docs[index - 1];
        const currentOrder = current.order || 0;
        const prevOrder = prev.order || 0;
        // تبديل قيم الترتيب
        try {
            await projectFirestore.collection(collection).doc(current.id).update({ order: prevOrder });
            await projectFirestore.collection(collection).doc(prev.id).update({ order: currentOrder });
        } catch (err) {
            console.error('Error reordering:', err);
        }
    };

    const handleMoveDown = async (index) => {
        if (index >= docs.length - 1) return;
        const current = docs[index];
        const next = docs[index + 1];
        const currentOrder = current.order || 0;
        const nextOrder = next.order || 0;
        try {
            await projectFirestore.collection(collection).doc(current.id).update({ order: nextOrder });
            await projectFirestore.collection(collection).doc(next.id).update({ order: currentOrder });
        } catch (err) {
            console.error('Error reordering:', err);
        }
    };

    const handleAutoAssignOrder = async () => {
        if (!window.confirm('سيتم تعيين ترتيب تلقائي لجميع الصور بناءً على ترتيبها الحالي. متأكد؟')) return;
        setReordering(true);
        try {
            const batch = projectFirestore.batch();
            docs.forEach((doc, index) => {
                const docRef = projectFirestore.collection(collection).doc(doc.id);
                // أكبر رقم = يظهر أولا (desc)
                batch.update(docRef, { order: Date.now() - (index * 1000) });
            });
            await batch.commit();
        } catch (err) {
            alert('حدث خطأ: ' + err.message);
        }
        setReordering(false);
    };

    return (
        <div className="container my-4">
            <h3 className="text-center mb-3">إدارة الصور</h3>

            <div className="d-flex justify-content-center align-items-center gap-3 mb-3 flex-wrap">
                <select className="form-select" style={{ maxWidth: 250 }} value={collection} onChange={e => setCollection(e.target.value)}>
                    {COLLECTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span className="badge bg-secondary">{docs.length} صورة</span>
                <button className="btn btn-sm btn-outline-info" onClick={handleAutoAssignOrder} disabled={reordering || docs.length === 0}>
                    {reordering ? 'جاري التعيين...' : '🔢 تعيين ترتيب تلقائي'}
                </button>
            </div>

            <div className="row g-3">
                {docs.map((doc, index) => {
                    const imgUrl = getDisplayUrl(doc);
                    const fallbacks = getFallbackUrls(doc);
                    const srcBadge = getSourceBadge(doc);
                    return (
                    <div className="col-md-3 col-sm-4 col-6" key={doc.id}>
                        <div className="card shadow-sm h-100">
                            <div style={{ position: 'relative', height: 150, overflow: 'hidden', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                onClick={() => setPreviewImg(imgUrl)}>
                                <img src={getThumbUrl(imgUrl, 300)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy"
                                    onError={(e) => handleImageError(e, fallbacks)} />
                                <span className="badge bg-dark" style={{ position: 'absolute', top: 4, right: 4, opacity: 0.7, fontSize: 10 }}>
                                    {index + 1}
                                </span>
                                <span title={srcBadge.title} style={{ position: 'absolute', bottom: 4, right: 4, background: srcBadge.bg, color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 4, opacity: 0.85 }}>
                                    {srcBadge.text}
                                </span>
                            </div>
                            <div className="card-body text-center p-1">
                                <div className="btn-group btn-group-sm w-100 mb-1">
                                    <button className="btn btn-outline-secondary" onClick={() => handleMoveUp(index)} disabled={index === 0} title="أعلى">
                                        ▲
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={() => handleMoveDown(index)} disabled={index >= docs.length - 1} title="أسفل">
                                        ▼
                                    </button>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-danger w-100"
                                    onClick={() => handleDelete(doc.id)}
                                    disabled={deleting[doc.id]}
                                >
                                    {deleting[doc.id] ? 'جاري الحذف...' : 'حذف'}
                                </button>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>

            {previewImg && (
                <div className="d-flex justify-content-center align-items-center" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, cursor: 'pointer' }}
                    onClick={() => setPreviewImg(null)}>
                    <img src={previewImg} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
                </div>
            )}
        </div>
    );
};

export default ImageManager;
